import { TranslationKey } from './../../../../../../types/translation/TranslationKey';
import { Grid } from '@mui/material';
import React, { useContext, useMemo, useState } from 'react';
import { t } from '../../../../../../types/translation/Translator';
import { CustomFieldContext } from '../../../../../../context/CustomFieldContext';
import { CustomField, CustomFieldEntityType, CustomFieldType } from '../../../../../../types/custom_field/customField';
import {
  codeToProductTransactionTriggerType,
  CreateTriggerInput,
  ProductTransactionTriggerType,
  productTransactionTriggerTypeToCode,
  productTransactionTriggerTypeToString,
  Trigger,
  TriggerEntityParentType,
  triggerEntityParentTypeToString,
  TriggerEntityType,
  triggerEntityTypeToString,
} from '../../../../../../types/trigger';
import { testIds } from '../../../../../../util/identifiers/identifiers.util';
import { BackButtonTemplate } from '../../../../../../VentoryUI/components/common/Button/Templates/BackButton';
import { NextButtonTemplate } from '../../../../../../VentoryUI/components/common/Button/Templates/NextButton/NextButton';
import ModalPane from '../../../../../../VentoryUI/components/common/Modal/components/ModalPane';
import Dropdown from '../../../../Common/Dropdown';
import TextInput from '../../../../Common/TextInput';
import { CompanyContext } from '../../../../../../context/CompanyContext';
import { Badges } from '../../../../../components/Badge';
import { DropdownSearchSelect } from '../../../../../components/Dropdown';
import { TextInput as NewTextInput } from '../../../../../components/TextInput';
import { CustomFieldUtil } from '../../../../../../types/custom_field';

interface CreateTriggerEntityTypePaneInputProps {
  trigger: CreateTriggerInput;
  setTrigger: (trigger: Trigger) => void;
  next: () => void;
  back: () => void;
}

export function CreateTriggerEntityTypePane({
  trigger,
  setTrigger,
  next,
  back,
}: CreateTriggerEntityTypePaneInputProps) {
  const { currentCompany } = useContext(CompanyContext);
  const { customFields } = useContext(CustomFieldContext);

  const entityTypeOptions = [TriggerEntityType.task];

  const hasExpiryManagement = currentCompany.settings.featureToggles.lots.expiryManagement;
  if (hasExpiryManagement) entityTypeOptions.push(TriggerEntityType.pmd);

  const dateCustomFields = [...customFields.values()].filter(cf => cf.type === CustomFieldType.date);
  if (dateCustomFields.length) entityTypeOptions.push(TriggerEntityType.customField);

  const productTransactionDateCustomFields = useMemo(() => {
    return [...customFields.values()].filter(
      cf => cf.entityType === CustomFieldEntityType.productTransaction && cf.type === CustomFieldType.date,
    );
  }, [customFields]);

  const getTriggerValue = () => {
    if (!trigger.entityValue) return '';
    if (trigger.entityValue.includes('${VALUE')) {
      const items = trigger.entityValue.split('${VALUE}');
      if (items.length < 2) return '';
      return items[1];
    } else if (trigger.entityValue.includes('${EXPIRY_DATE}')) {
      const items = trigger.entityValue.split('${EXPIRY_DATE}');
      if (items.length < 2) return '';
      return items[1];
    } else if (trigger.entityValue.includes('${DUE_DATE}')) {
      const items = trigger.entityValue.split('${DUE_DATE}');
      if (items.length < 2) return '';
      return items[1];
    } else if (trigger.entityValue.includes('${UPDATED_FIELD}')) {
      return undefined;
    } else if (trigger.entityValue.includes('${DATE_TRIGGER_VALUE}')) {
      const items = trigger.entityValue.split('${DATE_TRIGGER_VALUE}');
      if (items.length < 2) return '';
      return items[1];
    }
    return '';
  };

  const triggerValue = getTriggerValue();

  const content = () => {
    switch (trigger.entityType) {
      case TriggerEntityType.lot:
      case TriggerEntityType.pmd:
        return (
          <Grid item xs={12}>
            <TextInput
              type={'number'}
              mandatory
              label={t(TranslationKey.lotTriggerValueText).singular.label}
              value={triggerValue}
              onChange={value => setTrigger(trigger.withEntityValue('${EXPIRY_DATE}' + value))}
              testId={testIds.triggerExpiryValue}
            />
          </Grid>
        );

      case TriggerEntityType.productTransaction:
        return (
          <>
            <Grid item xs={12}>
              <Dropdown
                label={t(TranslationKey.entityParentType).singular.label}
                values={Object.values(TriggerEntityParentType)}
                onChange={() => {}}
                disabled={true}
                mandatory
                selectedValue={trigger.entityParentType}
                toText={el => (el ? triggerEntityParentTypeToString(el) : '')}
              />
            </Grid>
            <Grid item xs={12}>
              <Dropdown
                label={t(TranslationKey.triggerCondition).singular.label}
                values={[...Object.values(ProductTransactionTriggerType)]}
                mandatory
                selectedValue={codeToProductTransactionTriggerType(trigger.entityValue)}
                onChange={value =>
                  value && setTrigger(trigger.withEntityValue(productTransactionTriggerTypeToCode(value)))
                }
                toText={el => (el ? productTransactionTriggerTypeToString(el) : '')}
              />
            </Grid>

            {trigger.entityValue ? (
              <Grid item xs={12}>
                <Dropdown
                  label={t(TranslationKey.field).singular.label}
                  values={productTransactionDateCustomFields}
                  toText={el => (el ? el.name : '')}
                  mandatory
                  selectedValue={productTransactionDateCustomFields.find(el => el.id === trigger.customFieldIds.at(0))}
                  onChange={value => value && setTrigger(trigger.withCustomFieldId(value.id))}
                />
              </Grid>
            ) : null}
            {trigger.entityValue &&
            codeToProductTransactionTriggerType(trigger.entityValue) === ProductTransactionTriggerType.dueDate ? (
              <Grid item xs={12}>
                <TextInput
                  type={'number'}
                  mandatory
                  label={t(TranslationKey.productTransactionDueDateTriggerText).singular.label}
                  value={triggerValue}
                  onChange={value => {
                    setTrigger(trigger.withEntityValue('${DUE_DATE}' + value));
                  }}
                />
              </Grid>
            ) : null}
          </>
        );
      case TriggerEntityType.task:
        return (
          <>
            <Grid item xs={12}>
              <TextInput
                type={'number'}
                mandatory
                label={t(TranslationKey.taskDueDateTriggerValueText).singular.label}
                value={triggerValue}
                onChange={value => setTrigger(trigger.withEntityValue('${DUE_DATE}' + value))}
              />
            </Grid>
          </>
        );
      case TriggerEntityType.customField:
        return (
          <CreateTriggerEntityTypeCustomFieldView
            triggerValue={triggerValue}
            trigger={trigger}
            setTrigger={setTrigger}
            customFields={dateCustomFields}
          />
        );
    }
  };

  const canNext = () => {
    if (!trigger.entityType || !trigger.entityValue) return false;
    switch (trigger.entityType) {
      case TriggerEntityType.lot:
      case TriggerEntityType.pmd:
        return trigger.entityValue.split('${EXPIRY_DATE}').length > 1;
      case TriggerEntityType.productTransaction:
        if (!trigger.entityValue || !trigger.customFieldIds.length) return false;
        if (trigger.entityValue.includes('${UPDATED_FIELD}')) {
          return true;
        } else if (trigger.entityValue.includes('${DUE_DATE}')) {
          return trigger.entityValue.length !== 11;
        }

        return false;
      case TriggerEntityType.task:
        if (trigger.entityValue.includes('${DUE_DATE}')) {
          return trigger.entityValue.length !== 11;
        }

        return false;
      case TriggerEntityType.customField:
        if (trigger.entityValue.includes('${DATE_TRIGGER_VALUE}')) {
          return trigger.entityValue.length !== 21;
        }
    }
  };

  return (
    <ModalPane
      footerButtons={[BackButtonTemplate(back), NextButtonTemplate(next, { disabled: !canNext() })]}
      testId={testIds.createTriggerEntityTypePane}
    >
      <Grid container height={'100%'} alignContent={'space-between'}>
        <Grid item xs={12}>
          <Grid container rowSpacing={1}>
            <Grid item xs={12}>
              <Dropdown
                placeholder={t(TranslationKey.selectEntityTypeForTrigger).singular.upper}
                testId={testIds.triggerEntityType}
                mandatory
                label={t(TranslationKey.triggerEntityType).singular.label}
                values={entityTypeOptions}
                selectedValue={trigger.entityType}
                toText={type => triggerEntityTypeToString(type)}
                onChange={type => setTrigger(trigger.withEntityType(type))}
              />
            </Grid>
            {content()}
          </Grid>
        </Grid>
      </Grid>
    </ModalPane>
  );
}

interface CreateTriggerEntityTypeCustomFieldViewProps {
  triggerValue: string | undefined;
  trigger: Trigger;
  setTrigger: (trigger: Trigger) => void;
  customFields: CustomField[];
}

enum Modifier {
  before = 'before',
  dayOf = 'dayOf',
  after = 'after',
}

const modifierValues: Record<Modifier, -1 | 0 | 1> = {
  before: 1,
  dayOf: 0,
  after: -1,
} as const;

const modifierTranslations: Record<Modifier, TranslationKey> = {
  before: TranslationKey.before,
  dayOf: TranslationKey.dayOf,
  after: TranslationKey.after,
} as const;

function CreateTriggerEntityTypeCustomFieldView({
  triggerValue,
  trigger,
  setTrigger,
  customFields,
}: CreateTriggerEntityTypeCustomFieldViewProps) {
  const [modifier, setModifier] = useState(Modifier.dayOf);
  const modifierValue = modifierValues[modifier];

  const currentNumberValue = Math.abs(parseInt(triggerValue || '0'));

  const handleModifierChange = (value: Modifier) => {
    setModifier(value);
    if (value === Modifier.dayOf) return setTrigger(trigger.withEntityValue('${DATE_TRIGGER_VALUE}0'));
    const newTriggerValue = (currentNumberValue || 1) * modifierValues[value];
    setTrigger(trigger.withEntityValue('${DATE_TRIGGER_VALUE}' + newTriggerValue.toString()));
  };

  const selectedField = customFields.find(cf => cf.id === trigger.entityIds[0]);

  return (
    <Grid item xs={12}>
      <div className='grid gap-4'>
        <DropdownSearchSelect<CustomField>
          mandatory
          label={t(TranslationKey.field).singular.upper}
          textBuilder={cf => `${cf.name} (${CustomFieldUtil.customFieldEntityTypeToString(cf.entityType)})`}
          placeholder={t(TranslationKey.selectCustomField).singular.upper}
          value={selectedField}
          items={customFields}
          onSelect={cf => setTrigger(trigger.withEntityIds(cf ? [cf.id] : []))}
        />
        {!!selectedField && (
          <>
            <p className='text-sm text-body'>
              {t(TranslationKey.createTriggerEntityTypePaneWhenShouldYouBeNotified).singular.upper}
            </p>
            <Badges<Modifier>
              value={modifier}
              items={Object.values(Modifier)}
              textBuilder={item => t(modifierTranslations[item]).singular.upper}
              onSelect={handleModifierChange}
            />
            {modifier !== Modifier.dayOf && (
              <NewTextInput
                type={'number'}
                mandatory
                label={t(TranslationKey.day).plural.upper}
                value={currentNumberValue}
                onChange={value => {
                  const newTriggerValue = value ? parseInt(value) * modifierValue : '';
                  setTrigger(trigger.withEntityValue('${DATE_TRIGGER_VALUE}' + newTriggerValue));
                }}
              />
            )}
          </>
        )}
      </div>
    </Grid>
  );
}
